import { useState } from 'react'
import type { ProductImage } from '../../types'

interface ProductImageGalleryProps {
  primaryImageUrl: string
  /** gallery_images from API — can be string[] or ProductImage[] depending on source */
  galleryImages: Array<string | ProductImage>
  /** Structured images from PHP backend */
  images?: ProductImage[]
  alt: string
}

export default function ProductImageGallery({ primaryImageUrl, galleryImages, images, alt }: ProductImageGalleryProps) {
  const allImages: Array<{ url: string; alt: string }> = []
  const seen = new Set<string>()

  function addImage(url: string, imgAlt: string) {
    if (!url || seen.has(url)) return
    seen.add(url)
    allImages.push({ url, alt: imgAlt })
  }

  // Primary image first
  if (primaryImageUrl) addImage(primaryImageUrl, alt)

  // Structured images (from PHP backend ProductImage entities)
  if (images && images.length > 0) {
    for (const img of images) {
      addImage(img.url, img.alt || alt)
    }
  }

  // gallery_images — may be string URLs or ProductImage objects
  for (const item of galleryImages) {
    if (typeof item === 'string') {
      addImage(item, alt)
    } else if (item && typeof item === 'object' && 'url' in item) {
      addImage(item.url, item.alt || alt)
    }
  }

  // Fallback placeholder
  if (allImages.length === 0) {
    allImages.push({ url: '/placeholder-product.svg', alt })
  }

  const [selectedIndex, setSelectedIndex] = useState(0)
  const selected = allImages[selectedIndex] ?? allImages[0]

  return (
    <div className="flex flex-col gap-3">
      {/* Main image — above the fold on the PDP, so eager load + high
          fetch priority. CLS protection comes from the parent's
          aspect-[4/3] + min-h-[320px]. We deliberately do NOT set width/height
          attrs on the <img>: the CDN serves ~250px thumbnails, and declaring
          640x480 makes the browser bilinear-upscale a too-small source on
          retina (DPR 2/3), which looks blurry. The CSS aspect-ratio holds
          the box, the img scales to fit. */}
      <div className="flex items-center justify-center bg-white rounded-xl border border-neutral-200 p-8 min-h-[320px] aspect-[4/3]">
        <img
          src={selected.url}
          alt={selected.alt}
          loading="eager"
          fetchPriority="high"
          decoding="async"
          className="max-h-80 max-w-full object-contain"
          onError={e => { (e.target as HTMLImageElement).src = '/placeholder-product.svg' }}
        />
      </div>

      {/* Thumbnail strip — only show if multiple images */}
      {allImages.length > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-1">
          {allImages.map((img, i) => (
            <button
              key={`${img.url}-${i}`}
              onClick={() => setSelectedIndex(i)}
              className={`flex-shrink-0 w-16 h-16 rounded-lg border-2 p-1 transition-colors ${
                i === selectedIndex
                  ? 'border-primary bg-primary/5'
                  : 'border-neutral-200 hover:border-neutral-300'
              }`}
            >
              <img
                src={img.url}
                alt={img.alt}
                loading="lazy"
                decoding="async"
                width={64}
                height={64}
                className="w-full h-full object-contain"
                onError={e => { (e.target as HTMLImageElement).src = '/placeholder-product.svg' }}
              />
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
